import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type Cell, flexRender } from "@tanstack/react-table";
import { type CSSProperties } from "react";

import { cn } from "@/lib/utils/cn";

/**
 * Body cell registered with the same sortable id as the column header so horizontal
 * column reorder shows the entire column (all rows) moving together.
 */
export function DraggableColumnTableCell<TData>({
  cell,
  className,
  fixedColumnWidths = false,
}: {
  cell: Cell<TData, unknown>;
  className?: string;
  fixedColumnWidths?: boolean;
}) {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.65 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    transition: isDragging ? "none" : "transform 200ms ease-in-out",
    minWidth: cell.column.getSize(),
    ...(fixedColumnWidths
      ? {
          width: cell.column.getSize(),
          maxWidth: cell.column.getSize(),
        }
      : {}),
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <td ref={setNodeRef} style={style} className={cn(className)}>
      {fixedColumnWidths ? (
        <div className="min-w-0 truncate">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ) : (
        flexRender(cell.column.columnDef.cell, cell.getContext())
      )}
    </td>
  );
}
